import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useSessionPresence } from "@/hooks/useSessionPresence";
import { useSessionParticipants } from "@/hooks/useSessionParticipants";
import { useRealtimeMatrix } from "@/hooks/useRealtimeMatrix";
import { useSessionSteps } from "@/hooks/useSessionSteps";
import { Loader2, Menu, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { UserMenu } from "@/components/UserMenu";
import { CollaborationBar } from "@/components/session/CollaborationBar";
import { Phase3Intro } from "@/components/session/Phase3Intro";
import { DecisionMatrix } from "@/components/session/DecisionMatrix";
import { DominantStrategySummary } from "@/components/session/DominantStrategySummary";
import { Phase3Transition } from "@/components/session/Phase3Transition";
import { Phase1ContextPanel } from "@/components/session/Phase1ContextPanel";
import { PhaseNavigationDialog } from "@/components/session/PhaseNavigationDialog";
import { GlobalPhaseTimeline } from "@/components/session/GlobalPhaseTimeline";
import { DecisionCommitDialog } from "@/components/session/DecisionCommitDialog";
import { OptionsReferenceBar } from "@/components/session/OptionsReferenceBar";
import { ParticipantsPanel } from "@/components/session/ParticipantsPanel";
import { calculateAlignmentScore, calculateEffectiveExposure } from "@/lib/utils/alignmentScore";
import quentariLogo from "@/assets/quentari-logo.svg";

interface DominantStrategy {
  type: "clear_winner" | "conditional" | "trade_off" | "hidden_winner";
  option_name?: string;
  option_id?: string;
  explanation: string;
  key_insights: string[];
}

interface EnrichedMetrics {
  alignment_score?: number;
  is_constraint_break?: boolean;
  is_strategy_shift?: boolean;
  total_capacity_percent?: number;
  effective_daily_exposure?: number;
  alignment_deductions?: string[];
}

const Phase3 = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { activeUsers, updatePresence } = useSessionPresence(sessionId, user?.id || null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [dominantStrategy, setDominantStrategy] = useState<DominantStrategy | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isMatrixComplete, setIsMatrixComplete] = useState(false);
  const [showBackDialog, setShowBackDialog] = useState(false);
  const [showCommitDialog, setShowCommitDialog] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [tradeOffs, setTradeOffs] = useState<Array<{ optionName: string; criterion: string; impact: string }>>([]);
  const [excludedOptions, setExcludedOptions] = useState<Array<{ id: string; name: string; consequence: string; isDelayOption?: boolean }>>([]);
  const [dominantMetrics, setDominantMetrics] = useState<EnrichedMetrics | null>(null);
  const [commitMode, setCommitMode] = useState(false);
  const [sessionMeta, setSessionMeta] = useState<Record<string, any> | null>(null);
  const [matrixScores, setMatrixScores] = useState<Array<{ optionId: string; name: string; score: number; koFailed: boolean }>>([]);
  const { participants } = useSessionParticipants(sessionId);
  const { steps } = useSessionSteps(sessionId);
  
  // Realtime sync for matrix entries - triggers DecisionMatrix reload
  const matrixReloadRef = useRef<(() => void) | null>(null);
  useRealtimeMatrix(sessionId, user?.id || null, () => {
    matrixReloadRef.current?.();
  });
  
  // Broadcast subphase on mount
  useEffect(() => {
    updatePresence({ current_subphase: "3.0" });
  }, [updatePresence]);
  
  useEffect(() => {
    loadSessionData();
  }, [sessionId]);

  const loadSessionData = async () => {
    if (!sessionId) {
      navigate("/dashboard");
      return;
    }

    try {
      const { data: session, error } = await supabase
        .from("decision_sessions")
        .select("phase, workspace_id")
        .eq("id", sessionId)
        .single();

      if (error) throw error;

      if (session) {
        setWorkspaceId(session.workspace_id);
      }

      if (!session) {
        toast({
          title: "Session nicht gefunden",
          description: "Die Session konnte nicht geladen werden.",
          variant: "destructive",
        });
        navigate("/dashboard");
        return;
      }

      // Allow phase 2 or 3
      if (session.phase < 2) {
        toast({
          title: "Falsche Phase",
          description: "Diese Session ist noch nicht bereit für die Bewertung.",
          variant: "destructive",
        });
        navigate(`/app/session/${sessionId}`);
        return;
      }

      setIsLoading(false);
    } catch (error: any) {
      console.error("Error loading session:", error);
      toast({
        title: "Fehler",
        description: "Die Session-Daten konnten nicht geladen werden.",
        variant: "destructive",
      });
      navigate("/dashboard");
    }
  };

  const handleMatrixComplete = async (strategy: DominantStrategy | null) => {
    setIsMatrixComplete(true);
    setIsAnalyzing(true);

    // Load enriched metrics and matrix scores for the dominant option
    if (strategy?.option_id && sessionId) {
      try {
        const [{ data: scenario }, { data: session }, { data: matrixEntries }, { data: allScenarios }] = await Promise.all([
          supabase
            .from("scenarios")
            .select("is_constraint_break, is_strategy_shift, capacity_requirement_percent, reversibility_level, inaction_cost_effect, time_to_first_signal_days")
            .eq("id", strategy.option_id)
            .single(),
          supabase
            .from("decision_sessions")
            .select("meta")
            .eq("id", sessionId)
            .single(),
          supabase
            .from("decision_matrix_entries")
            .select("option_id, criterion, score, weight")
            .eq("session_id", sessionId),
          supabase
            .from("scenarios")
            .select("id, name")
            .eq("session_id", sessionId)
            .neq("scenario_type", "baseline"),
        ]);

        if (scenario && session) {
          const meta = (session.meta as Record<string, any>) || {};
          const irrev = meta.phase1_3_irreversibility || {};
          const conflict = meta.phase1_2_conflict || {};
          const isCommitMode = meta.phase2_commit_mode === true;
          setCommitMode(isCommitMode);
          setSessionMeta(meta);

          const baselineCapacity = conflict.capacity_percent || 0;
          const totalCapacity = baselineCapacity + (scenario.capacity_requirement_percent || 0);
          const validationDeadlineDays = irrev.validation_deadline_days || meta.validation_deadline_days;
          const dailyExposure = conflict.daily_exposure 
            || (irrev.exposure_until_validation && validationDeadlineDays 
                ? Math.round(irrev.exposure_until_validation / validationDeadlineDays) 
                : 0);

          const alignment = calculateAlignmentScore({
            is_constraint_break: scenario.is_constraint_break || false,
            is_strategy_shift: scenario.is_strategy_shift || false,
            total_capacity_percent: totalCapacity,
            reversibility_level: scenario.reversibility_level,
            inaction_cost_effect: scenario.inaction_cost_effect,
            commit_mode: isCommitMode,
            time_to_first_signal_days: scenario.time_to_first_signal_days,
            validation_deadline_days: validationDeadlineDays,
          });

          const effectiveExposure = calculateEffectiveExposure(dailyExposure, scenario.inaction_cost_effect);

          setDominantMetrics({
            alignment_score: alignment.score,
            alignment_deductions: alignment.deductions,
            is_constraint_break: scenario.is_constraint_break || false,
            is_strategy_shift: scenario.is_strategy_shift || false,
            total_capacity_percent: totalCapacity,
            effective_daily_exposure: effectiveExposure,
           });

          // Compute matrix scores for transition/dialog
          if (matrixEntries && allScenarios) {
            const criteria = meta.phase3_criteria || [];
            const uniqueCriteria = criteria.length > 0 
              ? criteria.map((c: any) => c.key || c.criterion)
              : [...new Set(matrixEntries.map((e: any) => e.criterion))];
            const numCriteria = uniqueCriteria.length;

            const scores = allScenarios.map((s: any) => {
              const entries = matrixEntries.filter((e: any) => e.option_id === s.id);
              let weightedTotal = 0;
              let koFailed = false;

              entries.forEach((entry: any) => {
                const idx = uniqueCriteria.indexOf(entry.criterion);
                const weight = idx === 0 ? 50 : (numCriteria > 1 ? 50 / (numCriteria - 1) : 50);
                const scoreVal = entry.score?.toLowerCase();
                const numeric = (scoreVal === 'high' || scoreVal === 'hoch') ? 3 :
                  (scoreVal === 'medium' || scoreVal === 'mittel') ? 2 : 1;
                weightedTotal += (numeric * weight) / 100;

                // K.O. check: index 0 criterion must be 'high'
                if (idx === 0 && scoreVal !== 'high' && scoreVal !== 'hoch') {
                  koFailed = true;
                }
              });

              return { optionId: s.id, name: s.name, score: weightedTotal * 100 / 3, koFailed };
            });

            setMatrixScores(scores);
          }
        }
      } catch (error) {
        console.error("Error loading dominant metrics:", error);
      }
    }

    setTimeout(() => {
      setDominantStrategy(strategy);
      setIsAnalyzing(false);
    }, 500);
  };

  // Open commit dialog - load trade-offs and build excluded options list
  const handleRequestCommit = async () => {
    try {
      // Load trade-offs from session meta
      const { data: session } = await supabase
        .from("decision_sessions")
        .select("meta")
        .eq("id", sessionId)
        .single();
      
      const meta = (session?.meta || {}) as Record<string, unknown>;
      const tradeOffReasons = (meta.trade_off_reasons || {}) as Record<string, string>;
      
      // Get all scenarios for display
      const { data: scenarios } = await supabase
        .from("scenarios")
        .select("id, name, archetype, strategic_goal")
        .eq("session_id", sessionId!)
        .neq("scenario_type", "baseline");
      
      const scenarioNames: Record<string, string> = {};
      scenarios?.forEach(s => { scenarioNames[s.id] = s.name; });
      
      // Format trade-offs for display
      const formattedTradeOffs = Object.entries(tradeOffReasons).map(([key, reason]) => {
        const [optionId] = key.split('-');
        return {
          optionName: scenarioNames[optionId] || 'Option',
          criterion: key.split('-').slice(1).join('-'),
          impact: reason
        };
      });
      
      // Build excluded options with real data instead of hardcoded consequences
      const formattedExcludedOptions = (scenarios || [])
        .filter(s => s.id !== dominantStrategy?.option_id)
        .map(s => {
          // Use strategic_goal as real consequence language
          const consequence = s.strategic_goal 
            ? `Kein Fokus auf: ${s.strategic_goal}`
            : "Keine Ressourcen für diese Richtung";
          const isDelayOption = s.archetype === "scaling";
          
          return {
            id: s.id,
            name: s.name,
            consequence,
            isDelayOption
          };
        });
      
      setTradeOffs(formattedTradeOffs);
      setExcludedOptions(formattedExcludedOptions);
      setShowCommitDialog(true);
    } catch (error) {
      console.error("Error loading trade-offs:", error);
      setShowCommitDialog(true);
    }
  };

  // Handle the actual commit
  const handleCommit = async (commitData: {
    committed_option_id: string;
    committed_option_name: string;
    validity_days: 30 | 60 | 90;
    owner_name: string;
    review_date: string;
    forced_commit?: boolean;
    forced_commit_active_users?: string[];
  }) => {
    try {
      // Get current session meta
      const { data: session } = await supabase
        .from("decision_sessions")
        .select("meta, workspace_id")
        .eq("id", sessionId)
        .single();
      
      const currentMeta = (session?.meta || {}) as Record<string, unknown>;
      
      // Build commit metadata including forced commit audit trail
      const decisionCommit: Record<string, unknown> = {
        ...commitData,
        committed_at: new Date().toISOString(),
      };

      if (commitData.forced_commit) {
        decisionCommit.forced_commit = true;
        decisionCommit.forced_commit_active_users = commitData.forced_commit_active_users;
        decisionCommit.forced_commit_timestamp = new Date().toISOString();
      }

      // Save commit data to session meta
      await supabase
        .from("decision_sessions")
        .update({ 
          phase: 3, 
          subphase: "3.4",
          meta: JSON.parse(JSON.stringify({
            ...currentMeta,
            decision_commit: decisionCommit,
          }))
        })
        .eq("id", sessionId);

      // Log forced commit to activity_logs for audit trail
      if (commitData.forced_commit && session?.workspace_id && user?.id) {
        await supabase.from("activity_logs").insert({
          workspace_id: session.workspace_id,
          user_id: user.id,
          activity_type: "commit_forced_due_to_active_editors",
          entity_type: "decision_session",
          entity_id: sessionId!,
          metadata: {
            committed_option: commitData.committed_option_name,
            active_users_at_commit: commitData.forced_commit_active_users,
            active_user_count: commitData.forced_commit_active_users?.length || 0,
          },
        });
      }

      // FUNDAMENT 5: Create immutable commit record with full governance data
      if (session?.workspace_id && user?.id) {
        try {
          // Load report, participants, criteria, weights, and input snapshot in parallel
          const [
            { data: reportData },
            { data: participantsData },
            { data: latestSnapshot },
            { data: criteriaData },
            { data: weightsData },
          ] = await Promise.all([
            supabase
              .from("decision_reports")
              .select("id, version_number, metadata")
              .eq("session_id", sessionId)
              .maybeSingle(),
            supabase
              .from("decision_participants")
              .select("user_id, role")
              .eq("session_id", sessionId!),
            (supabase.from("report_generation_inputs" as any) as any)
              .select("id")
              .eq("session_id", sessionId!)
              .order("created_at", { ascending: false })
              .limit(1)
              .maybeSingle(),
            (supabase.from("decision_criteria" as any) as any)
              .select("criterion_key, label, is_ko, weight:decision_criteria_weights(weight, version)")
              .eq("session_id", sessionId!)
              .is("superseded_by", null),
            (supabase.from("decision_criteria_weights" as any) as any)
              .select("criterion_id, weight, version")
              .eq("session_id", sessionId!)
              .order("version", { ascending: false }),
          ]);

          // Compute commit hash from commit data
          const commitPayloadStr = JSON.stringify({ ...decisionCommit, session_id: sessionId });
          const hashBuffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(commitPayloadStr));
          const commitHash = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');

          // Extract alignment score and decision thesis from session meta
          const sessionMeta = currentMeta as Record<string, any>;
          const decisionThesis = sessionMeta?.decision_thesis?.thesis || sessionMeta?.decision_thesis?.kernsatz || null;
          
          // Build data quality from report metadata
          const reportMeta = (reportData?.metadata || {}) as Record<string, any>;
          const dataQuality = reportMeta?.data_quality || null;

          // Build session snapshot for full auditability
          const sessionSnapshot = {
            schema_version: "session_snapshot_v1",
            formula_version: "v1_ko50",
            captured_at: new Date().toISOString(),
            criteria: criteriaData || [],
            weights: weightsData || [],
            data_quality: dataQuality,
            decision_thesis: sessionMeta?.decision_thesis || null,
            kill_criterion: sessionMeta?.kill_criterion || null,
            commit_mode: sessionMeta?.phase1_commit_mode || null,
            phase1_3_irreversibility: sessionMeta?.phase1_3_irreversibility || null,
          };

          await (supabase.from("decision_commits" as any) as any).insert({
            session_id: sessionId!,
            report_id: reportData?.id || null,
            report_version: reportData?.version_number || 1,
            committed_by: user.id,
            commit_type: commitData.forced_commit ? 'forced' : 'standard',
            active_editors_at_commit: commitData.forced_commit_active_users || [],
            participants_snapshot: participantsData || [],
            input_snapshot_id: (latestSnapshot as any)?.id || null,
            commit_hash: commitHash,
            commit_data: decisionCommit,
            // Step 4.2 + 4.3: Governance-Erweiterung
            alignment_score_at_commit: dominantMetrics?.alignment_score ?? null,
            decision_thesis_at_commit: decisionThesis,
            session_snapshot: sessionSnapshot,
          });

          // Build decision memory (fire-and-forget)
          supabase.functions.invoke('build-decision-memory', {
            body: { session_id: sessionId!, workspace_id: session.workspace_id },
          }).catch(err => console.warn('Memory build failed (non-critical):', err));

          // FUNDAMENT 6: Log commit_created activity
          await supabase.rpc('log_activity', {
            _workspace_id: session.workspace_id,
            _user_id: user.id,
            _activity_type: 'commit_created',
            _entity_type: 'decision_session',
            _entity_id: sessionId!,
            _metadata: { 
              committed_option: commitData.committed_option_name, 
              commit_type: commitData.forced_commit ? 'forced' : 'standard',
              commit_hash: commitHash,
            },
          });
        } catch (commitErr) {
          console.warn("Failed to create commit record (non-critical):", commitErr);
        }
      }

      setShowCommitDialog(false);
      
      toast({
        title: "Entscheidung committed",
        description: `${commitData.committed_option_name} wurde verbindlich festgelegt.`,
      });
      
      navigate(`/app/session/${sessionId}/report`);
    } catch (error: any) {
      console.error("Error committing decision:", error);
      toast({
        title: "Fehler",
        description: "Die Entscheidung konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    }
  };

  const [targetPhase, setTargetPhase] = useState<number | null>(null);

  const handlePhaseNavigation = (phase: number) => {
    setTargetPhase(phase);
    setShowBackDialog(true);
  };

  const handleConfirmNavigation = async () => {
    if (!sessionId || targetPhase === null) return;
    
    try {
      if (targetPhase === 1) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 1, subphase: "1.4" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}`);
      } else if (targetPhase === 2) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 2, subphase: "2.0" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}/phase2`);
      } else if (targetPhase === 4) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 3, subphase: "3.4" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}/report`);
      }
    } catch (error: any) {
      console.error("Error navigating to phase:", error);
      toast({
        title: "Fehler",
        description: "Navigation fehlgeschlagen.",
        variant: "destructive",
      });
    }
  };

  const getPhaseLabel = (phase: number) => {
    const labels: Record<number, string> = {
      1: "Phase 1 – Analyse",
      2: "Phase 2 – Optionen",
      3: "Phase 3 – Bewertung",
      4: "Report"
    };
    return labels[phase] || `Phase ${phase}`;
  };

  // Computed props for Phase3Transition and DecisionCommitDialog
  const optionScoresForTransition = matrixScores.map(s => ({
    name: s.name,
    score: s.score,
    isDominant: s.optionId === dominantStrategy?.option_id,
    koFailed: s.koFailed,
  }));

  const optionScoresForDialog = matrixScores.map(s => ({
    name: s.name,
    score: s.score,
    isKoFailed: s.koFailed,
  }));

  const koStatusForDialog = (() => {
    const koCriterion = sessionMeta?.phase3_criteria?.[0];
    if (!koCriterion) return undefined;
    
    const dominantOptionScore = matrixScores.find(s => s.optionId === dominantStrategy?.option_id);
    const passed = !dominantOptionScore?.koFailed;
    
    // Check if ALL options failed K.O.
    const allOptionsFailed = matrixScores.length > 0 && matrixScores.every(s => s.koFailed);
    // Which options passed K.O.
    const passingOptions = matrixScores.filter(s => !s.koFailed).map(s => s.name);
    
    return {
      criterion: koCriterion.label || koCriterion.key,
      passed,
      allOptionsFailed,
      passingOptions,
    };
  })();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div 
            className="absolute top-0 left-0 w-full h-full opacity-[0.02]"
            style={{
              backgroundImage: `
                radial-gradient(ellipse 60% 40% at 10% 20%, hsl(var(--primary)) 0%, transparent 50%),
                radial-gradient(ellipse 50% 50% at 90% 80%, hsl(var(--primary)) 0%, transparent 50%)
              `,
            }}
          />
        </div>
        <div className="text-center relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto mb-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
          <p className="text-muted-foreground">Phase 3 wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-[0.02]"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 60% 40% at 10% 20%, hsl(var(--primary)) 0%, transparent 50%),
              radial-gradient(ellipse 50% 50% at 90% 80%, hsl(var(--primary)) 0%, transparent 50%)
            `,
          }}
        />
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="container flex h-16 items-center justify-between max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 md:gap-4">
            <img 
              src={quentariLogo} 
              alt="Quentari" 
              className="h-8 w-auto cursor-pointer transition-transform duration-300 hover:scale-105" 
              onClick={() => navigate('/dashboard')}
            />
            <div className="h-6 w-px bg-border hidden sm:block" />
            <GlobalPhaseTimeline
              currentPhase={3}
              sessionId={sessionId!}
              onPhaseClick={handlePhaseNavigation}
            />
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Button variant="ghost" size="sm" className="gap-2 hover:bg-primary/10 hover:text-primary" onClick={() => setIsParticipantsOpen(true)}>
              <Users className="h-4 w-4" />
              Teilnehmer ({participants.length})
            </Button>
            <CollaborationBar users={activeUsers} steps={steps} currentSubphase="3.0" />
            <UserMenu />
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-primary/10 hover:text-primary">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] border-border/50">
                <div className="flex flex-col gap-6 mt-6">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                    <span className="text-sm font-medium">Phasen-Navigation</span>
                    <div className="mt-3">
                      <GlobalPhaseTimeline
                        currentPhase={3}
                        sessionId={sessionId!}
                        onPhaseClick={handlePhaseNavigation}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Button variant="ghost" size="sm" className="w-full justify-start gap-2" onClick={() => setIsParticipantsOpen(true)}>
                      <Users className="h-4 w-4" />
                      Teilnehmer ({participants.length})
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <span className="text-sm font-medium">Aktive Nutzer</span>
                    <CollaborationBar users={activeUsers} steps={steps} currentSubphase="3.0" />
                  </div>
                  <div className="border-t border-border/50 pt-4">
                    <UserMenu />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="container max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12 relative z-10">

        <div className="space-y-6">
          {/* Phase 1 Context Panel */}
          <Phase1ContextPanel sessionId={sessionId!} />

          {/* Options Reference Bar - sticky above matrix */}
          <OptionsReferenceBar sessionId={sessionId!} />

          {/* 3.1 - Intro */}
          <Phase3Intro commitMode={commitMode} />

          {/* 3.2 - Decision Matrix */}
          <div className="space-y-4">
            <DecisionMatrix 
              sessionId={sessionId!} 
              onMatrixComplete={handleMatrixComplete}
            />
          </div>

          {/* 3.3 - Dominant Strategy (shows after matrix is complete) */}
          {(dominantStrategy || isAnalyzing) && (
            <div className="animate-fade-in">
              <DominantStrategySummary 
                dominantStrategy={dominantStrategy}
                isLoading={isAnalyzing}
                metrics={dominantMetrics}
                matrixScores={matrixScores}
                matrixDivergenceExplanation={sessionMeta?.recommendation_matrix_divergence_explanation}
              />
            </div>
          )}

          {/* 3.4 - Transition */}
          <Phase3Transition 
            onContinue={handleRequestCommit} 
            isDisabled={!isMatrixComplete}
            dominantOptionName={dominantStrategy?.option_name}
            isConstraintBreak={dominantMetrics?.is_constraint_break}
            alignmentScore={dominantMetrics?.alignment_score}
            thesisSummary={(sessionMeta as any)?.decision_thesis || (sessionMeta as any)?.phase1_4_thesis}
            optionScores={optionScoresForTransition}
          />
        </div>
      </main>

      {/* Decision Commit Dialog */}
      <DecisionCommitDialog
        open={showCommitDialog}
        onOpenChange={setShowCommitDialog}
        dominantOptionId={dominantStrategy?.option_id || ""}
        dominantOptionName={dominantStrategy?.option_name || "Dominante Option"}
        sessionId={sessionId!}
        workspaceId={workspaceId || ""}
        tradeOffs={tradeOffs}
        excludedOptions={excludedOptions}
        onCommit={handleCommit}
        alignmentScore={dominantMetrics?.alignment_score}
        thesisSummary={(sessionMeta as any)?.decision_thesis || (sessionMeta as any)?.phase1_4_thesis}
        koStatus={koStatusForDialog}
        optionScores={optionScoresForDialog}
      />

      {/* Phase Navigation Dialog */}
      <PhaseNavigationDialog
        open={showBackDialog}
        onOpenChange={setShowBackDialog}
        onConfirm={handleConfirmNavigation}
        fromPhase="Phase 3 – Bewertung"
        toPhase={getPhaseLabel(targetPhase || 2)}
      />

      <ParticipantsPanel
        sessionId={sessionId!}
        workspaceId={workspaceId}
        open={isParticipantsOpen}
        onOpenChange={setIsParticipantsOpen}
      />
    </div>
  );
};

export default Phase3;
